import { useRef, useCallback, useEffect } from 'react';

type Section = { id: string; anchor: string };

type ScrollMetrics = {
  scrollTop: number;
  scrollHeight: number;
  clientHeight: number;
  topOffset: number;
};

/**
 * Check if element is visible and has passed the top offset
 */
function isSectionActive(element: HTMLElement, topOffset: number, clientHeight: number): boolean {
  const rect = element.getBoundingClientRect();
  const isVisible = rect.bottom > topOffset && rect.top < clientHeight;
  const hasPassedTop = rect.top <= topOffset + 50;
  return isVisible && hasPassedTop;
}

/**
 * Check if element is visible in viewport
 */
function isSectionVisible(element: HTMLElement, topOffset: number, clientHeight: number): boolean {
  const rect = element.getBoundingClientRect();
  return rect.bottom > topOffset && rect.top < clientHeight;
}

/**
 * Find the first active section (visible and past top offset)
 */
function findActiveSectionId(
  sections: Section[],
  topOffset: number,
  clientHeight: number
): string | null {
  for (const section of sections) {
    const element = document.getElementById(section.anchor);
    if (element && isSectionActive(element, topOffset, clientHeight)) {
      return section.id;
    }
  }
  return null;
}

/**
 * Find the first visible section (fallback when no section is past top offset)
 */
function findFirstVisibleSectionId(
  sections: Section[],
  topOffset: number,
  clientHeight: number
): string | null {
  for (const section of sections) {
    const element = document.getElementById(section.anchor);
    if (element && isSectionVisible(element, topOffset, clientHeight)) {
      return section.id;
    }
  }
  return null;
}

/**
 * Get scroll metrics for the current page
 */
function getScrollMetrics(topOffset: number): ScrollMetrics {
  return {
    scrollTop: window.pageYOffset || document.documentElement.scrollTop,
    scrollHeight: document.documentElement.scrollHeight,
    clientHeight: document.documentElement.clientHeight,
    topOffset,
  };
}

/**
 * Determine target section based on scroll position
 */
function determineTargetSection(sections: Section[], metrics: ScrollMetrics): string | null {
  const { scrollTop, scrollHeight, clientHeight, topOffset } = metrics;

  // Near top of page
  if (scrollTop < 50) {
    return sections[0]?.id ?? null;
  }

  // Near bottom of page
  if (scrollTop + clientHeight >= scrollHeight - 100) {
    return sections.at(-1)?.id ?? null;
  }

  // Middle sections: find first active or first visible
  return (
    findActiveSectionId(sections, topOffset, clientHeight) ||
    findFirstVisibleSectionId(sections, topOffset, clientHeight)
  );
}

/**
 * Update active section if it has changed
 */
function updateActiveSectionIfChanged(
  targetSectionId: string | null,
  activeSection: string,
  lastSectionIdRef: { current: string },
  setActiveSection: (id: string) => void
): void {
  if (!targetSectionId) return;
  if (targetSectionId === lastSectionIdRef.current) return;

  lastSectionIdRef.current = targetSectionId;
  if (activeSection !== targetSectionId) {
    setActiveSection(targetSectionId);
  }
}

/**
 * Enhanced scroll spy hook that handles navigation clicks
 * Temporarily disables scroll spy during programmatic scrolling
 */
export function useScrollSpyWithNavigation(
  sections: Section[],
  activeSection: string,
  setActiveSection: (id: string) => void,
  options?: { topOffset?: number; bottomOffsetRatio?: number }
) {
  const isNavigatingRef = useRef(false);
  const navigationTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const lastSectionIdRef = useRef<string>(activeSection);

  // Handle scroll position detection
  useEffect(() => {
    const handleScroll = () => {
      if (isNavigatingRef.current) return;

      const topOffset = options?.topOffset ?? 50;
      const metrics = getScrollMetrics(topOffset);
      const targetSectionId = determineTargetSection(sections, metrics);

      updateActiveSectionIfChanged(
        targetSectionId,
        activeSection,
        lastSectionIdRef,
        setActiveSection
      );
    };

    // Initial check
    handleScroll();

    // Listen to scroll events with throttle
    let ticking = false;
    const onScroll = () => {
      if (!ticking) {
        globalThis.requestAnimationFrame(() => {
          handleScroll();
          ticking = false;
        });
        ticking = true;
      }
    };

    window.addEventListener('scroll', onScroll, { passive: true });

    return () => {
      window.removeEventListener('scroll', onScroll);
      // Clear timeout on cleanup to prevent memory leaks
      if (navigationTimeoutRef.current) {
        clearTimeout(navigationTimeoutRef.current);
        navigationTimeoutRef.current = null;
      }
    };
  }, [sections, activeSection, setActiveSection, options]);

  // Handle navigation click
  const handleSectionClick = useCallback(
    (sectionId: string) => {
      // Immediately update active section and ref
      setActiveSection(sectionId);
      lastSectionIdRef.current = sectionId;

      // Clear any existing timeout
      if (navigationTimeoutRef.current) {
        clearTimeout(navigationTimeoutRef.current);
      }

      // Find and scroll to section
      const section = sections.find((s) => s.id === sectionId);
      if (section) {
        const element = document.getElementById(section.anchor);
        if (element) {
          const topOffset = options?.topOffset ?? 50;

          // Disable scroll spy during navigation
          isNavigatingRef.current = true;

          const y = element.getBoundingClientRect().top + window.pageYOffset - topOffset;
          window.scrollTo({ top: y, behavior: 'smooth' });

          // Re-enable scroll spy after scroll completes
          setTimeout(() => {
            isNavigatingRef.current = false;
            navigationTimeoutRef.current = null;
          }, 1000);
        }
      }
    },
    [sections, setActiveSection, options]
  );

  return { handleSectionClick };
}
